import React, { useCallback, useEffect, useRef } from 'react';
import { Typography, Box, Button, Chip } from '@mui/material';
import { useValue } from '@tldraw/state-react';
import { useEditor, stopEventPropagation } from 'tldraw';
import { T } from '@tldraw/validate';
import { BaseBoxShapeUtil, HTMLContainer } from 'tldraw';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import SmartToyOutlinedIcon from '@mui/icons-material/SmartToyOutlined';
import classNames from 'classnames';
import LazyImage from 'components/LazyImage';
import { getCardImageHoverScale } from 'constants/cardImageZoom';
import { useBoardAiContext } from '../context/BoardAiContext';
import { extractBoardDocumentSnapshot } from '../utils/boardSnapshot';
import { usePinCardInViewport } from '../hooks/usePinCardInViewport';
import { isCanvasStrokeInProgress } from '../utils/canvasToolState';
import { startBoardPinPointerInteraction } from '../utils/pinCardDrag';
import {
  SUGGEST_PIN_WIDTH,
  SUGGEST_PIN_MIN_HEIGHT,
} from '../constants/suggestPinLayout';
import { resolveDisplayTitle } from '../utils/pinTitle';
import { openPinContextMenu } from '../utils/openTldrawContextMenu';
import {
  boardInvZoomPx,
  boardPinHeaderHeightPx,
  boardPinHeaderScreenPx,
  readBoardInvZoom,
} from '../utils/boardCameraCss';

const BOARD_HEADER_SCALE = 0.8;

const suggestPinShapeProps = {
  w: T.nonZeroNumber,
  h: T.nonZeroNumber,
  url: T.string,
  title: T.string,
  snippet: T.string,
  imageUrl: T.string,
  reason: T.string,
  searchQuery: T.string,
};

function useAutoSuggestPinHeight(editor, shape) {
  const contentRef = useRef(null);
  const rafRef = useRef(null);

  useEffect(() => {
    const el = contentRef.current;
    if (!el) return undefined;

    const syncHeight = () => {
      if (isCanvasStrokeInProgress(editor)) return;
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(() => {
        const currentShape = editor.getShape(shape.id);
        if (!currentShape) return;

        const measured = Math.ceil(el.scrollHeight);
        if (measured < SUGGEST_PIN_MIN_HEIGHT) return;

        const nextHeight = Math.max(measured, SUGGEST_PIN_MIN_HEIGHT);
        if (Math.abs(nextHeight - currentShape.props.h) <= 2) return;

        editor.updateShape({
          id: shape.id,
          type: 'suggest-pin',
          props: { h: nextHeight },
        });
      });
    };

    const resizeObserver = new ResizeObserver(syncHeight);
    resizeObserver.observe(el);
    syncHeight();

    return () => {
      resizeObserver.disconnect();
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [
    editor,
    shape.id,
    shape.props.w,
    shape.props.url,
    shape.props.title,
    shape.props.snippet,
    shape.props.reason,
    shape.props.imageUrl,
  ]);

  return contentRef;
}

function useAutoSuggestPinWidth(editor, shape) {
  useEffect(() => {
    if (isCanvasStrokeInProgress(editor)) return undefined;

    const currentShape = editor.getShape(shape.id);
    if (!currentShape || Math.abs(SUGGEST_PIN_WIDTH - currentShape.props.w) <= 2) return undefined;

    editor.updateShape({
      id: shape.id,
      type: 'suggest-pin',
      props: { w: SUGGEST_PIN_WIDTH },
    });
  }, [editor, shape.id, shape.props.w]);
}

function SuggestPinComponent({ shape }) {
  const editor = useEditor();
  const { onSaveSnapshot } = useBoardAiContext();
  const { url, title, snippet, imageUrl, reason } = shape.props;
  const displayTitle = resolveDisplayTitle(title, url, { snippet });
  const lastTransformOriginRef = useRef('center');

  const boardSpacePx = px => `${px * BOARD_HEADER_SCALE}px`;

  const allowPinInteraction = useValue(
    'suggest-pin-pointer-events',
    () => editor.isIn('select'),
    [editor]
  );
  const loadImageWhen = usePinCardInViewport(editor, shape);
  const contentRef = useAutoSuggestPinHeight(editor, shape);
  useAutoSuggestPinWidth(editor, shape);
  const showReason = Boolean(reason) && reason !== snippet;

  const handlePinContextMenuCapture = useCallback(
    event => {
      openPinContextMenu(editor, shape.id, event);
    },
    [editor, shape.id]
  );

  const handleOpenSource = useCallback(
    e => {
      stopEventPropagation(e);
      if (!url) return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    [url]
  );

  const bringHoveredPinToFront = useCallback(() => {
    editor.run(() => {
      editor.bringToFront([shape.id]);
    }, { history: 'ignore' });
  }, [editor, shape.id]);

  const saveSuggestPinSnapshot = useCallback(() => {
    try {
      onSaveSnapshot?.(extractBoardDocumentSnapshot(editor));
    } catch {
      /* ignore */
    }
  }, [editor, onSaveSnapshot]);

  const handleHeaderPointerDown = useCallback(
    event => {
      if (!allowPinInteraction) return;
      startBoardPinPointerInteraction(editor, event, {
        shapeId: shape.id,
        shapeType: 'suggest-pin',
        historyLabel: 'suggest pin',
        allowDrag: true,
        onDrop: saveSuggestPinSnapshot,
      });
    },
    [editor, shape.id, allowPinInteraction, saveSuggestPinSnapshot]
  );

  const handleBodyPointerDown = useCallback(
    event => {
      // Allow click-to-open for viewers (readonly); drag only when editable.
      if (!allowPinInteraction && !editor.getInstanceState().isReadonly) return;
      startBoardPinPointerInteraction(editor, event, {
        shapeId: shape.id,
        shapeType: 'suggest-pin',
        historyLabel: 'suggest pin',
        allowDrag: allowPinInteraction && !editor.getInstanceState().isReadonly,
        onClick: () => {
          if (!url) return;
          window.open(url, '_blank', 'noopener,noreferrer');
        },
        onDrop: saveSuggestPinSnapshot,
      });
    },
    [editor, shape.id, allowPinInteraction, url, saveSuggestPinSnapshot]
  );

  return (
    <HTMLContainer
      id={shape.id}
      className="board-suggest-pin-container"
      style={{
        width: shape.props.w,
        height: shape.props.h,
        pointerEvents: 'none',
        overflow: 'visible',
      }}
      onContextMenuCapture={handlePinContextMenuCapture}
    >
      <div ref={contentRef} className="board-suggest-pin-measure">
        <Box
          className={classNames('board-suggest-pin', {
            'board-suggest-pin--pass-through': !allowPinInteraction,
          })}
          onMouseEnter={allowPinInteraction ? bringHoveredPinToFront : undefined}
          sx={{
            padding: boardSpacePx(10),
            gap: boardSpacePx(8),
            borderRadius: boardSpacePx(14),
          }}
        >
          <Box
            className="board-suggest-pin-drag-handle"
            onPointerDown={allowPinInteraction ? handleHeaderPointerDown : undefined}
            sx={{
              boxSizing: 'border-box',
              height: boardPinHeaderHeightPx(),
              minHeight: boardPinHeaderHeightPx(),
              maxHeight: boardPinHeaderHeightPx(),
              gap: boardInvZoomPx(8),
              paddingBottom: 0,
              overflow: 'hidden',
              alignItems: 'center',
              cursor: allowPinInteraction ? 'grab' : undefined,
              '&:active': allowPinInteraction ? { cursor: 'grabbing' } : undefined,
            }}
          >
            <Chip
              size="small"
              icon={<SmartToyOutlinedIcon sx={{ fontSize: boardPinHeaderScreenPx() }} />}
              label="Suggested"
              className="board-suggest-pin-kind"
              sx={{
                height: boardInvZoomPx(22),
                maxHeight: boardInvZoomPx(22),
                fontSize: boardPinHeaderScreenPx(),
                minWidth: 0,
                maxWidth: '100%',
                '& .MuiChip-label': {
                  px: boardInvZoomPx(6),
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                },
                '& .MuiChip-icon': { ml: boardInvZoomPx(4), flexShrink: 0 },
              }}
            />
          </Box>

          <Box
            className="board-suggest-pin-body"
            onPointerDown={allowPinInteraction ? handleBodyPointerDown : undefined}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: boardSpacePx(8),
              minWidth: 0,
              cursor: allowPinInteraction ? 'pointer' : undefined,
            }}
          >
            {imageUrl ? (
              <Box
                className="board-suggest-pin-image-area"
                sx={{ borderRadius: boardSpacePx(10) }}
              >
                <LazyImage
                  src={imageUrl}
                  when={loadImageWhen}
                  lazy={false}
                  referrerPolicy="no-referrer"
                  wrapperClassName="board-suggest-pin-image-wrap"
                  wrapperStyle={{ width: '100%', height: '100%' }}
                  style={{
                    width: '100%',
                    height: '100%',
                    objectFit: 'cover',
                    display: 'block',
                    transition: 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)',
                    transformOrigin: 'center',
                  }}
                  onMouseEnter={e => {
                    e.target.style.transition = 'transform 0.5s ease-in';
                    e.target.style.transform = `scale(${getCardImageHoverScale('BOARD', readBoardInvZoom(e.target))})`;
                  }}
                  onMouseMove={e => {
                    const rect = e.target.getBoundingClientRect();
                    const x = ((e.clientX - rect.left) / rect.width) * 100;
                    const y = ((e.clientY - rect.top) / rect.height) * 100;
                    const origin = `${x}% ${y}%`;
                    e.target.style.transformOrigin = origin;
                    lastTransformOriginRef.current = origin;
                  }}
                  onMouseLeave={e => {
                    e.target.style.transformOrigin = lastTransformOriginRef.current;
                    e.target.style.transition = 'transform 1s cubic-bezier(0.4, 0, 0.2, 1)';
                    e.target.style.transform = 'scale(1)';
                  }}
                />
              </Box>
            ) : null}

            <Typography
              className="board-suggest-pin-title"
              noWrap
              sx={{
                fontSize: boardPinHeaderScreenPx(),
                lineHeight: 1.2,
                overflow: 'hidden',
                textOverflow: 'ellipsis',
                whiteSpace: 'nowrap',
              }}
            >
              {displayTitle}
            </Typography>

            {snippet ? (
              <Typography
                className="board-suggest-pin-snippet"
                sx={{ fontSize: '7px', lineHeight: 1.35 }}
              >
                {snippet.slice(0, 140)}
              </Typography>
            ) : null}

            {showReason ? (
              <Typography
                className="board-suggest-pin-reason"
                sx={{ fontSize: '7px', lineHeight: 1.35 }}
              >
                {reason.slice(0, 100)}
              </Typography>
            ) : null}
          </Box>

          <Box className="board-suggest-pin-actions" sx={{ gap: boardSpacePx(6), marginTop: boardSpacePx(2) }}>
            <Button
              size="small"
              variant="contained"
              startIcon={<OpenInNewIcon sx={{ fontSize: boardSpacePx(16) }} />}
              onPointerDown={stopEventPropagation}
              onClick={url ? handleOpenSource : undefined}
              sx={{
                textTransform: 'none',
                fontSize: boardSpacePx(12),
                lineHeight: 1.25,
                minHeight: boardSpacePx(30),
                px: boardSpacePx(10),
                py: boardSpacePx(4),
                borderRadius: boardSpacePx(8),
                '& .MuiButton-startIcon': {
                  marginLeft: boardSpacePx(-2),
                  marginRight: boardSpacePx(6),
                  '& > *:nth-of-type(1)': { fontSize: boardSpacePx(16) },
                },
              }}
            >
              Open source
            </Button>
          </Box>
        </Box>
      </div>
    </HTMLContainer>
  );
}

export class SuggestPinShapeUtil extends BaseBoxShapeUtil {
  static type = 'suggest-pin';

  static props = suggestPinShapeProps;

  canResize() {
    return false;
  }

  hideResizeHandles() {
    return true;
  }

  hideRotateHandle() {
    return true;
  }

  hideSelectionBoundsFg() {
    return true;
  }

  hideSelectionBoundsBg() {
    return true;
  }

  getDefaultProps() {
    return {
      w: SUGGEST_PIN_WIDTH,
      h: SUGGEST_PIN_MIN_HEIGHT,
      url: '',
      title: '',
      snippet: '',
      imageUrl: '',
      reason: '',
      searchQuery: '',
    };
  }

  component(shape) {
    return <SuggestPinComponent shape={shape} />;
  }

  indicator(shape) {
    return (
      <rect
        width={shape.props.w}
        height={shape.props.h}
        rx={14}
        ry={14}
      />
    );
  }
}
